import * as React from "react";
import { splitEscaped } from "./split";

// Power of a two-sided z-test for the difference between two independent
// runs, against the true gap in points, with observed gaps marked on it. It
// answers "how big would a real difference have to be before this sample could
// see it?" -- the curve is flat and low where the observed gaps sit.
//
// Props match the MDX registry entry:
// `se`    standard error of ONE run's pass rate, in points (e.g. an instance
//         bootstrap SE). The gap's SE is taken as se * sqrt(2).
// `gaps`  observed gaps in points to mark on the curve
// `names` optional, comma-separated name per gap (write "\," for a literal comma)
//
// Normal approximation, alpha 0.05 two-sided. The 80% power point is computed
// and printed; no number on the curve is passed in.

export interface PowerCurveProps {
  se: number;
  gaps: number[];
  names?: string;
}

const Z = 1.959964;
const W = 400;
const H = 230;
const PAD = { l: 38, r: 14, t: 12, b: 34 };

// Abramowitz & Stegun 7.1.26, |error| < 1.5e-7.
function erf(x: number): number {
  const s = x < 0 ? -1 : 1;
  const a = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * a);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-a * a);
  return s * y;
}
const phi = (x: number) => 0.5 * (1 + erf(x / Math.SQRT2));

export const powerAt = (gap: number, seGap: number) => phi(gap / seGap - Z) + phi(-gap / seGap - Z);

const fmt = (n: number) => Number(n.toFixed(1)).toString();

export function PowerCurve({ se, gaps, names }: PowerCurveProps) {
  const uid = React.useId();
  const ok = Number.isFinite(se) && se > 0;
  const seGap = ok ? se * Math.SQRT2 : 1;
  const mde = (Z + 0.841621) * seGap; // 80% power
  const nameList = (names ?? "") === "" ? [] : splitEscaped(names ?? "", ",").map((s) => s.trim());
  // Power is symmetric in the sign of the gap, so use |gap|. Each name stays
  // paired with its own gap: bad entries are dropped AFTER pairing.
  const marks = (gaps ?? [])
    .map((g, i) => ({ g: Math.abs(g), name: nameList[i] ?? "" }))
    .filter((m) => Number.isFinite(m.g));

  if (!ok) {
    return (
      <div className="gc-widget pw-widget">
        <div className="gc-widget-label">{"// "}power curve — no data</div>
      </div>
    );
  }

  // capped so a huge gap cannot create thousands of ticks
  const xMax = Math.min(200, Math.max(10, Math.ceil((Math.max(mde, ...marks.map((m) => m.g)) * 1.25) / 10) * 10));
  const x = (g: number) => PAD.l + (g / xMax) * (W - PAD.l - PAD.r);
  const y = (p: number) => PAD.t + (1 - p) * (H - PAD.t - PAD.b);
  const steps = 80;
  const path = Array.from({ length: steps + 1 }, (_, i) => {
    const g = (i / steps) * xMax;
    return `${i === 0 ? "M" : "L"}${x(g).toFixed(1)},${y(powerAt(g, seGap)).toFixed(1)}`;
  }).join(" ");
  const xTicks = Array.from({ length: xMax / 10 + 1 }, (_, i) => i * 10);

  return (
    <div className="gc-widget pw-widget">
      <div className="gc-widget-label" aria-hidden="true">
        {"// "}power vs true gap — 80% power needs a gap of about {Math.round(mde)} points
      </div>
      <svg className="pw-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-labelledby={`${uid}t ${uid}d`}>
        <title id={`${uid}t`}>Power against the true gap in pass rate</title>
        <desc id={`${uid}d`}>
          {`Two-sided test at 5 percent, two independent runs, standard error ${fmt(seGap)} points. 80 percent power needs a gap of about ${Math.round(mde)} points. `}
          {marks.map((m) => `${m.name || "gap"} ${fmt(m.g)} points: power ${Math.round(powerAt(m.g, seGap) * 100)} percent. `)}
        </desc>
        {[0, 0.25, 0.5, 0.75, 1].map((p) => (
          <g key={p} aria-hidden="true">
            <line className="pw-grid" x1={PAD.l} x2={W - PAD.r} y1={y(p)} y2={y(p)} />
            <text className="pw-tick" x={PAD.l - 6} y={y(p) + 4} textAnchor="end">
              {Math.round(p * 100)}%
            </text>
          </g>
        ))}
        {xTicks.map((g) => (
          <text key={g} className="pw-tick" x={x(g)} y={H - PAD.b + 16} textAnchor="middle" aria-hidden="true">
            {g}
          </text>
        ))}
        <text className="pw-tick" x={(PAD.l + W - PAD.r) / 2} y={H - 4} textAnchor="middle" aria-hidden="true">
          true gap between two configurations (points)
        </text>
        <line className="pw-target" x1={PAD.l} x2={W - PAD.r} y1={y(0.8)} y2={y(0.8)} aria-hidden="true" />
        <line className="pw-target" x1={x(mde)} x2={x(mde)} y1={y(0.8)} y2={H - PAD.b} aria-hidden="true" />
        <path className="pw-curve" d={path} aria-hidden="true" />
        {marks.map(({ g, name }, i) => {
          const p = powerAt(g, seGap);
          // alternate above/below so neighbouring gaps (e.g. 7.4 and 9.3) do not overprint
          const above = i % 2 === 0;
          return (
            <g key={i} aria-hidden="true">
              <line className="pw-obs" x1={x(g)} x2={x(g)} y1={y(p)} y2={H - PAD.b} />
              <circle className="pw-dot" cx={x(g)} cy={y(p)} r={4} />
              <text
                className="pw-lab"
                x={above ? x(g) : x(g) + 9}
                y={above ? y(p) - 9 : y(p) + 11}
                textAnchor={above ? "middle" : "start"}
              >
                {name} {fmt(g)}
              </text>
            </g>
          );
        })}
      </svg>
      <table className="viz-sr">
        <caption>Power at each observed gap</caption>
        <thead>
          <tr>
            <th scope="col">gap</th>
            <th scope="col">gap in points</th>
            <th scope="col">power</th>
          </tr>
        </thead>
        <tbody>
          {marks.map((m, i) => (
            <tr key={i}>
              <th scope="row">{m.name || `gap ${i + 1}`}</th>
              <td>{fmt(m.g)}</td>
              <td>{Math.round(powerAt(m.g, seGap) * 100)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="gc-legend" aria-hidden="true">
        <span>— power of the test</span>
        <span>┄ 80% power</span>
        <span>● observed gap</span>
      </div>
    </div>
  );
}

export default PowerCurve;
